import { ImageOff } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import type { LightboxPhoto } from './PhotoLightbox';
import { useAuthedImageState } from './useAuthedImage';

interface Props {
  photo: LightboxPhoto;
  /** Guest-Bearer-Token: wenn gesetzt, wird das Bild auth-geschuetzt geladen. */
  token?: string;
  failedLabel: string;
  retryLabel: string;
}

const FRAME = 'max-h-[85vh] max-w-full rounded-lg object-contain';

/**
 * Das Bild im Vollbild. Bis das große Bild da ist, steht das kleine in derselben Größe mit einem
 * Ladezeichen darüber; scheitert das große, sagt es das und bietet einen neuen Versuch an — statt
 * eines Kreisels, der nie aufhört, oder eines hochgezogenen Vorschaubilds ohne Hinweis.
 */
export function LightboxImage({ photo, token, failedLabel, retryLabel }: Props) {
  const full = useAuthedImageState(token ? photo.src : null, token);
  const placeholder = useAuthedImageState(
    token && !full.src && !full.failed ? (photo.placeholder ?? null) : null,
    token,
  );
  const alt = photo.alt ?? '';

  // Ohne Token: src direkt (generischer Modus).
  if (!token) return <img src={photo.src} alt={alt} className={FRAME} />;
  if (full.failed) {
    return (
      <div role="alert" className="flex flex-col items-center gap-3 text-center text-white">
        <ImageOff className="size-8 text-white/70" aria-hidden />
        <p className="text-sm">{failedLabel}</p>
        <button
          type="button"
          onClick={full.retry}
          className="min-h-11 rounded-full bg-white/15 px-5 text-sm font-semibold text-white hover:bg-white/25"
        >
          {retryLabel}
        </button>
      </div>
    );
  }
  if (full.src) return <img src={full.src} alt={alt} className={FRAME} onError={full.broken} />;
  return (
    <div
      className={cn(
        'relative flex items-center justify-center',
        placeholder.src && 'w-[min(800px,100%)]',
      )}
    >
      {placeholder.src && (
        <img
          src={placeholder.src}
          alt={alt}
          className={cn(FRAME, 'w-full')}
          onError={placeholder.broken}
        />
      )}
      <div
        className={cn(
          'size-12 animate-spin rounded-full border-2 border-white/30 border-t-white',
          placeholder.src && 'absolute',
        )}
        aria-hidden
      />
    </div>
  );
}
